/**
 * One read shared by every screen that needs it, for a short while.
 *
 * The home page and the « Projets » block of the sidebar read the very same
 * two lists — the Odoo projects of the catalog and the ArgoCD Applications of
 * their environments — and both are mounted at once on the home page. Without
 * a shared read each list would be asked for twice on every visit.
 *
 * Module state, not a React context: the two consumers sit in different
 * subtrees (the nav is rendered by the app's layout, the page by its routes),
 * and a value held at module level is the one thing both reach without a
 * provider mounted above them.
 *
 * What is kept is the promise, not the value, so two callers arriving while
 * the request is in flight share it too. A promise that rejects is dropped as
 * soon as it settles: the next call retries instead of serving the failure
 * for the rest of the TTL.
 *
 * Subscribers (`subscribe`) hear of every answer fetched from the network,
 * whoever asked for it — not of a cached answer handed out again, which
 * brings nothing new. That is how the sidebar learns what the home page, a
 * poll or a Refresh elsewhere just read, without a request of its own.
 */

export interface SharedLoadOptions {
  /** Ignore a cached answer, however recent — the Refresh button. */
  force?: boolean;
  /**
   * Reuse a cached answer only when younger than this, when it is shorter
   * than the TTL — the « being deleted » polls (`branchDeletion.ts`), which
   * want an answer more recent than the TTL guarantees while still sharing
   * one request between the sidebar and the home page ticking close
   * together.
   */
  maxAgeMs?: number;
}

export interface SharedLoad<T> {
  load(fetcher: () => Promise<T>, options?: SharedLoadOptions): Promise<T>;
  /**
   * Calls `listener` with every value fetched from now on, once it has
   * resolved — unless a newer load (forced, or after `forget`) replaced it
   * meanwhile: a late older answer must not overwrite a newer one. Returns
   * the unsubscribe.
   */
  subscribe(listener: (value: T) => void): () => void;
  /** The next `load` fetches, whatever the age of the cached answer. */
  forget(): void;
  /** `forget`, and the last answer too (`latest` undefined again). */
  clear(): void;
  /**
   * The last answer fetched, whatever its age — what the subscribers were
   * last told. Undefined before the first. Asks nothing.
   */
  latest(): T | undefined;
}

export function createSharedLoad<T>(
  ttlMs: number,
  // Read at each call, not captured: a test's clock (`jest.spyOn(Date,
  // 'now')`) applies to the module-level loads too.
  now: () => number = () => Date.now(),
): SharedLoad<T> {
  let entry: { at: number; promise: Promise<T> } | undefined;
  const listeners = new Set<(value: T) => void>();
  let last: T | undefined;

  return {
    load(fetcher, options = {}) {
      const at = now();
      const maxAge = Math.min(ttlMs, options.maxAgeMs ?? ttlMs);
      if (!options.force && entry && at - entry.at < maxAge) {
        return entry.promise;
      }

      const current = { at, promise: fetcher() };
      entry = current;
      // A handled branch of its own, so the eviction never surfaces as an
      // unhandled rejection; the caller still receives the rejection through
      // the promise returned below. Compared by identity: a forced reload
      // started meanwhile must not be evicted by the older one failing, and
      // the older one's late answer is not announced over it.
      current.promise.then(
        value => {
          if (entry !== current) {
            return;
          }
          last = value;
          // A copy: a listener unsubscribing while being told must not skip
          // the next one.
          for (const listener of [...listeners]) {
            // A listener that throws must not fail the others, nor the read
            // shared by every caller — the same rule as `onPipelineAnswer`.
            try {
              listener(value);
            } catch {
              // Its own business; the others are still told.
            }
          }
        },
        () => {
          if (entry === current) {
            entry = undefined;
          }
        },
      );
      return current.promise;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    forget() {
      entry = undefined;
    },
    clear() {
      entry = undefined;
      last = undefined;
    },
    latest() {
      return last;
    },
  };
}
